import React from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { motion } from "framer-motion";
import { fadeInTitleTopVariant } from "../utils/variants";
import { BackgroundElementServices } from "../components/GlobalStyle";
import signature from "../assets/signature.webp";

const cardVariant = {
  hidden: { opacity: 0, y: 24, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const About = () => {
  const { t } = useTranslation();

  return (
    <MainContainer>
      <motion.h1
        variants={fadeInTitleTopVariant}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, amount: 0.15 }}
      >
        {t("about.title")}
      </motion.h1>

      <BackgroundElementServices className="red-1" />
      <BackgroundElementServices className="blue-1" />
      <BackgroundElementServices className="red-2" />
      <BackgroundElementServices className="blue-2" />

      <SectionAbout>
        <ArticleAbout
          as={motion.article}
          variants={cardVariant}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.15 }}
        >
          <Text>{t("about.intro")}</Text>
        </ArticleAbout>

        <ArticleAbout
          as={motion.article}
          variants={cardVariant}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.15 }}
        >
          <TitleText>{t("about.approach_title")}</TitleText>
          <Text>{t("about.approach_text")}</Text>
        </ArticleAbout>

        <ArticleAbout
          as={motion.article}
          variants={cardVariant}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.15 }}
        >
          <TitleText>{t("about.elegance_title")}</TitleText>
          <Text>{t("about.elegance_text")}</Text>
        </ArticleAbout>

        <ArticleAbout
          as={motion.article}
          variants={cardVariant}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.15 }}
        >
          <TitleText>{t("about.offer_title")}</TitleText>
          <ul>
            <ListText>{t("about.offer_text_one")}</ListText>
            <ListText>{t("about.offer_text_two")}</ListText>
            <ListText>{t("about.offer_text_three")}</ListText>
          </ul>
        </ArticleAbout>

        <ArticleAbout
          as={motion.article}
          variants={cardVariant}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.15 }}
        >
          <TitleText>{t("about.adventure_title")}</TitleText>
          <Text>{t("about.adventure_text")}</Text>
        </ArticleAbout>

        <SignatureWrapper>
          <SignatureImage src={signature} alt="signature" />
        </SignatureWrapper>
      </SectionAbout>
    </MainContainer>
  );
};

export default About;

/* ─── Styles ──────────────────────────────────────────────────────────────── */

const MainContainer = styled.main`
  padding: 6rem 1.5rem 5rem 1.5rem;

  @media (max-width: 768px) {
    padding: 5rem 1.25rem 4rem 1.25rem;
  }

  @media (max-width: 480px) {
    padding: 5rem 1rem 3rem 1rem;
  }
`;

const SectionAbout = styled.section`
  padding-top: 2rem;
  max-width: 900px;
  margin: auto;
  text-align: center;
`;

const ArticleAbout = styled.article`
  padding: 2.5rem 2rem;
  margin-bottom: 2rem;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  transition:
    transform 0.3s ease,
    border-color 0.3s ease;

  /* Fallback : visible par défaut pour navigateurs sans scroll-driven support */
  opacity: 1;
  scale: 1;

  /* Scroll-driven animation — uniquement si supporté */
  @supports (animation-timeline: scroll()) {
    scale: 0.95;
    opacity: 0;
    animation: fade-in linear forwards;
    animation-timeline: view();
    animation-range: entry 10% cover 35%;

    @keyframes fade-in {
      to {
        scale: 1;
        opacity: 1;
      }
    }
  }

  &:hover {
    transform: translateY(-5px);
    border-color: rgba(180, 1, 18, 0.3);
  }

  @media (max-width: 768px) {
    padding: 1.75rem 1.25rem;
    margin-bottom: 1.25rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
    border-radius: 14px;
  }
`;

const Text = styled.p`
  font-size: 1.2rem;
  color: #a8a29e;
  line-height: 1.8;
  letter-spacing: 0.02em;
  max-width: 650px;
  margin: 0.8rem auto 0 auto;

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
  }
`;

const TitleText = styled.h2`
  text-transform: uppercase;
  font-size: 1.6rem;
  margin-bottom: 1rem;
  letter-spacing: 0.08em;
  background: linear-gradient(to right, #ffffff, rgba(180, 1, 18, 1));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  position: relative;

  &::after {
    content: "";
    display: block;
    width: 40px;
    height: 2px;
    margin: 0.8rem auto 0 auto;
    background: rgba(180, 1, 18, 0.6);
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }

  @media (max-width: 480px) {
    font-size: 1.15rem;
  }
`;

const ListText = styled.li`
  list-style: none;
  position: relative;
  font-size: 1.2rem;
  color: #a8a29e;
  line-height: 1.8;
  margin: 0.5rem 0;
  padding-left: 1.5rem;
  text-align: left;

  &::before {
    content: "•";
    position: absolute;
    left: 0;
    color: rgba(180, 1, 18, 1);
    font-size: 1.4rem;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
  }
`;

const SignatureWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const SignatureImage = styled.img`
  width: 30%;
  opacity: 0.8;
  filter: brightness(1.2) contrast(1.1);
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;

  &:hover {
    opacity: 1;
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    width: 140px;
  }

  @media (max-width: 480px) {
    width: 110px;
  }
`;
